/**
 * Deployment Loader
 *
 * Loads contract addresses from deployment JSON files.
 * JSON files are copied from perpetual-dex/deployments/ via:
 *   node scripts/load-deployment.js
 *
 * Usage:
 *   import { getDeployment, getPerpetualDexContracts } from '@/deployments';
 */

import { ChainIdEnum } from "../enums/chain";
import { PerpetualDexContracts } from "../config/chains/types";

// Import deployment JSON files
// These are copied from perpetual-dex/deployments/ by load-deployment.js
let deployment123420001114: any = null;
let deployment6343: any = null;
let deployment6281971: any = null;
let deployment5042002: any = null;
let deployment10143: any = null;
let deployment97: any = null;

try {
  deployment123420001114 = require("./123420001114.json");
} catch {
  // Deployment file not found - will use fallback
}

try {
  deployment6343 = require("./6343.json");
} catch {
  // Deployment file not found - will use fallback
}

try {
  deployment6281971 = require("./6281971.json");
} catch {
  // Deployment file not found - will use fallback
}

try {
  deployment5042002 = require("./5042002.json");
} catch {
  // Deployment file not found - will use fallback
}

try {
  deployment10143 = require("./10143.json");
} catch {
  // Deployment file not found - will use fallback
}

try {
  deployment97 = require("./97.json");
} catch {
  // Deployment file not found - will use fallback
}

// Map chain IDs to their deployment data
const DEPLOYMENTS: Record<number, any> = {
  123420001114: deployment123420001114,
  6343: deployment6343,
  6281971: deployment6281971,
  5042002: deployment5042002,
  10143: deployment10143,
  97: deployment97,
};

/**
 * Deployment JSON structure from perpetual-dex
 */
export interface DeploymentJson {
  chainId: number;
  network: string;
  startBlock: number;
  deployedAt?: number;
  deployer?: string;
  rpcUrl?: string;
  core: {
    orderHandler: string;
    positionHandler: string;
    marketFactory: string;
    marketHandler: string;
    unifiedVault: string;
    unifiedMarginAdapter?: string;
    backstopVault: string;
    oracle: string;
    dataStore: string;
    batchOrderManager?: string;
    insuranceFund?: string;
    crossMargin?: string;
    perpRouter?: string;
    gaslessPerpRouter?: string;
    agentRegistry?: string;
  };
  markets: {
    btcPerp: string;
    ethPerp: string;
    [key: string]: string;
  };
  tokens: {
    usdc: string;
    usdcDecimals?: number;
  };
  spot?: {
    router?: string;
    balanceManager?: string;
    poolManager?: string;
    swapRouter?: string;
    quoter?: string;
  };
  perp?: {
    engine?: string;
    router?: string;
    gaslessRouter?: string;
    marketFactory?: string;
    oracle?: string;
    dataStore?: string;
    backstopVault?: string;
    proxyVault?: string;
  };
  PROXY_BALANCEMANAGER?: string;
  MOCK_TOKEN_USDC?: string; // flat-manifest fallback for chains without a `tokens` object
  AGENT_REGISTRY?: string;
  PERP_GASLESS_ROUTER?: string;
  QUANTUM_RISK_ENGINE?: string;
}

/**
 * Get raw deployment data for a chain
 */
export const getDeployment = (chainId: ChainIdEnum): DeploymentJson | null => {
  return DEPLOYMENTS[chainId] || null;
};

/**
 * Check if deployment exists for a chain
 */
export const hasDeployment = (chainId: ChainIdEnum): boolean => {
  return DEPLOYMENTS[chainId] !== null && DEPLOYMENTS[chainId] !== undefined;
};

/** Keys under `markets` that are contracts, not tradable instruments. */
const NON_MARKET_KEYS = new Set(["handlerPerp"]);

/**
 * Convert deployment JSON to PerpetualDexContracts format
 */
export const getPerpetualDexContractsFromDeployment = (
  chainId: ChainIdEnum
): PerpetualDexContracts | undefined => {
  const deployment = getDeployment(chainId);
  if (!deployment) return undefined;

  return {
    router: deployment.spot?.router || deployment.core?.perpRouter || deployment.core?.orderHandler || deployment.perp?.router || "",
    crossMargin: deployment.core?.crossMargin || "",
    orderHandler: deployment.core?.orderHandler || deployment.perp?.router || "",
    orderVault: "",
    marketFactory: deployment.core?.marketFactory || deployment.perp?.marketFactory || "",
    usdc: deployment.tokens?.usdc || deployment.MOCK_TOKEN_USDC || "",
    unifiedVault: deployment.core?.unifiedVault || deployment.perp?.proxyVault || "",
    unifiedMarginAdapter: deployment.core?.unifiedMarginAdapter || "",
    positionHandler: deployment.core?.positionHandler || deployment.perp?.router || "",
    backstopVault: deployment.core?.backstopVault || deployment.perp?.backstopVault || "",
    oracle: deployment.core?.oracle || deployment.perp?.oracle || "",
    dataStore: deployment.core?.dataStore || deployment.perp?.dataStore || "",
    marketHandler: deployment.core?.marketHandler || "",
    batchOrderManager: deployment.core?.batchOrderManager || "",
    balanceManager: deployment.spot?.balanceManager || deployment.PROXY_BALANCEMANAGER || "",
    perpRouter: deployment.core?.perpRouter || deployment.perp?.router || "",
    gaslessPerpRouter:
      deployment.core?.gaslessPerpRouter ||
      deployment.perp?.gaslessRouter ||
      deployment.PERP_GASLESS_ROUTER ||
      "",
    agentRegistry:
      deployment.core?.agentRegistry ||
      deployment.AGENT_REGISTRY ||
      "",
    quantumRiskEngine: deployment.QUANTUM_RISK_ENGINE || "",
    spotDomainName: chainId === ChainIdEnum.MONAD_TESTNET ? "CLOBEngine" : "CLOBRouter",
    perpDomainName: chainId === ChainIdEnum.MONAD_TESTNET ? "PerpEngine" : "CLOBPerpetualRouter",
    markets: Object.entries(deployment.markets || {}).reduce<Record<string, string>>(
      (acc, [key, value]) => {
        // `handlerPerp` is the MarketHandler contract, not a market: turning it
        // into a "HANDLER-PERP" instrument invents a market that does not exist.
        if (NON_MARKET_KEYS.has(key)) return acc;
        const instrumentName = key.endsWith("Perp")
          ? `${key.slice(0, -4).toUpperCase()}-PERP`
          : key;
        acc[instrumentName] = value;
        return acc;
      },
      {}
    ),
  };
};

/**
 * Get USDC address from deployment
 */
export const getUsdcAddressFromDeployment = (
  chainId: ChainIdEnum
): string | undefined => {
  const deployment = getDeployment(chainId);
  return deployment?.tokens?.usdc || deployment?.MOCK_TOKEN_USDC;
};

/**
 * Get USDC decimals from deployment
 */
export const getUsdcDecimalsFromDeployment = (
  chainId: ChainIdEnum
): number => {
  const deployment = getDeployment(chainId);
  return deployment?.tokens?.usdcDecimals ?? 6;
};

/**
 * Get token decimals config from deployment
 */
export const getTokenDecimalsFromDeployment = (chainId: ChainIdEnum) => {
  const usdcDecimals = getUsdcDecimalsFromDeployment(chainId);
  return {
    usdc: usdcDecimals,
    usdt: 6,
    weth: 18,
    wbtc: 8,
    sdai: 6,
  };
};

/**
 * Get market address from deployment
 */
export const getMarketAddressFromDeployment = (
  chainId: ChainIdEnum,
  marketSymbol: string
): string | undefined => {
  const deployment = getDeployment(chainId);
  if (!deployment) return undefined;

  const marketKey = marketSymbol.endsWith("-PERP")
    ? `${marketSymbol.replace("-PERP", "").toLowerCase()}Perp`
    : marketSymbol;

  return deployment.markets?.[marketSymbol] || deployment.markets?.[marketKey];
};

/**
 * Get RPC URL from deployment (if available)
 */
export const getRpcUrlFromDeployment = (
  chainId: ChainIdEnum
): string | undefined => {
  const deployment = getDeployment(chainId);
  return deployment?.rpcUrl;
};

/**
 * Get start block from deployment
 */
export const getStartBlockFromDeployment = (chainId: ChainIdEnum): number => {
  const deployment = getDeployment(chainId);
  return deployment?.startBlock ?? 0;
};
